<template>
  <div class="prose max-w-none">
    <h2 class="text-3xl font-black text-slate-800 mb-8">
      Herzlich Willkommen bei der EEG Taufkirchen/Nord!
    </h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
      <!-- Linke Spalte: Text & Icons -->
      <div class="space-y-6">
        Energiegemeinschaften (GEA, EEG, BEG) haben in Österreich in den
        vergangenen Jahren zahlenmäßig ein rasantes Wachstum verzeichnet, Ende
        2025 waren bereits mehr als 6500 Erneuerbare-Energie-Gemeinschaften und
        Bürgerenergiegemeinschaften in Betrieb.

        <div
          class="flex justify-center items-center space-x-32 w-full mb-6 mt-8"
        >
          <img
            :src="sonneEnergie"
            class="h-28 md:h-30 w-auto object-contain transition-transform hover:scale-105"
            alt="PV Energie"
          />
          <img
            :src="trafoIcon"
            class="h-28 md:h-28 w-auto object-contain transition-transform hover:scale-105"
            alt="E-Tankstelle"
          />
          <img
            :src="sonneTankstelle"
            class="h-28 md:h-30 w-auto object-contain transition-transform hover:scale-105"
            alt="E-Tankstelle"
          />
        </div>

        <p class="leading-relaxed text-slate-700">
          Unsere Energiegemeinschaft erzeugt mit Hilfe der Mitglieder lokal
          <span class="text-eeg-green font-medium">Strom aus Sonnenenergie</span
          >. Dieser wird
          <span class="font-semibold text-slate-900"
            >bedarfsorientiert und dynamisch</span
          >
          direkt in der Nachbarschaft verteilt.
        </p>

        <p class="leading-relaxed text-slate-700">
          Das Einzugsgebiet erstreckt sich rund um den Trafo 5002, der neben der
          Berndoblersraße - Höhe ISG Bauten - steht. Das heißt Erzeuger bzw.
          Verbraucher, die mit diesem Trafo verbunden sind, können bei der
          <i>EEG Taufkirchen/Nord</i> Mitglied werden.
        </p>
      </div>

      <!-- Rechte Spalte -->
      <div class="space-y-6">
        <!-- Ansicht 1: Standardmäßig die ImageCard -->
        <div v-if="activeView === 'tra'">
          <ImageCard
            title="Detailansicht: Alle Objekte mit grüner Färbung gehören zum Einzugsgebiet."
            :imageSrc="trafo"
            caption="Einzugsgebiet Trafo 5002"
          />
        </div>

        <!-- Ansicht 2: Großes Diagramm 'Lastverlauf' mit eigener Legende -->
        <div
          v-else-if="activeView === 'lastverlauf'"
          class="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3"
        >
          <div class="flex justify-between items-center">
            <h4 class="text-sm font-bold text-slate-800">
              Lastverlauf (Mitglied)
            </h4>
            <button
              @click="activeView = 'tra'"
              class="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center space-x-1"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                ></path>
              </svg>
              <span>Zurück zum Einzugsgebiet</span>
            </button>
          </div>

          <img
            :src="lastverlauf"
            class="w-full h-auto max-h-[350px] object-contain rounded-lg border border-slate-100 bg-slate-50 p-2"
            alt="Lastverlauf Großansicht"
          />

          <!-- Permanente Legende für Lastverlauf -->
          <div
            class="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 mt-4"
          >
            <h5
              class="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center space-x-2"
            >
              <svg
                class="w-4 h-4 text-eeg-green"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span>Legende</span>
            </h5>

            <div
              class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600"
            >
              <div
                class="flex items-start space-x-3 bg-white p-3 rounded-lg border border-slate-100 shadow-xs"
              >
                <span
                  class="w-3 h-3 rounded-full bg-emerald-700 mt-0.5 flex-shrink-0"
                ></span>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Dunkelgrüne Balken (Erzeugt)</strong
                  >
                  Stromproduktion (z.B. PV-Anlage) mit Spitzenwerten bis fast
                  500 kWh/Tag.
                </div>
              </div>
              <div
                class="flex items-start space-x-3 bg-white p-3 rounded-lg border border-slate-100 shadow-xs"
              >
                <span
                  class="w-3 h-3 rounded-full bg-emerald-400 mt-0.5 flex-shrink-0"
                ></span>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Hellgrüne Abschnitte (EEG)</strong
                  >
                  Direkt vor Ort genutzter EEG-Strom zur Deckung des
                  Eigenbedarfs.
                </div>
              </div>
              <div
                class="flex items-start space-x-3 bg-white p-3 rounded-lg border border-slate-100 shadow-xs"
              >
                <span
                  class="w-3 h-3 rounded-full bg-pink-400 mt-0.5 flex-shrink-0"
                ></span>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Pinke Abschnitte (EVU)</strong
                  >
                  Zusätzlicher Netzbezug vom Energieversorger (meist unter 100
                  kWh/Tag).
                </div>
              </div>
              <div
                class="flex items-start space-x-3 bg-white p-3 rounded-lg border border-slate-100 shadow-xs"
              >
                <span
                  class="w-3 h-3 rounded-full bg-slate-800 mt-0.5 flex-shrink-0"
                ></span>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Gesamthöhe & Linien</strong
                  >
                  Gestapelte Balken = Gesamtverbrauch. Linien = Anzahl aktiver
                  Marktteilnehmer.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Ansicht 3: Großes Diagramm 'Tagesverlauf' mit eigener Legende -->
        <div
          v-else-if="activeView === 'tagesverlauf'"
          class="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3"
        >
          <div class="flex justify-between items-center">
            <h4 class="text-sm font-bold text-slate-800">
              Tagesverlauf Erzeugung
            </h4>
            <button
              @click="activeView = 'tra'"
              class="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center space-x-1"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                ></path>
              </svg>
              <span>Zurück zum Einzugsgebiet</span>
            </button>
          </div>

          <img
            :src="tagesverlauf"
            class="w-full h-auto max-h-[350px] object-contain rounded-lg border border-slate-100 bg-slate-50 p-2"
            alt="Tagesverlauf Großansicht"
          />

          <!-- Permanente Legende für Tagesverlauf (Hier kannst du den Text für das zweite Bild anpassen) -->
          <div
            class="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 mt-4"
          >
            <h5
              class="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center space-x-2"
            >
              <svg
                class="w-4 h-4 text-eeg-green"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span>
                Tagesverlauf im Stunden-Raster, aufsummiert für den gesamten
                Monat Juli 2026</span
              >
            </h5>

            <div
              class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600"
            >
              <div
                class="flex items-start space-x-3 bg-white p-3 rounded-lg border border-slate-100 shadow-xs"
              >
                <span
                  class="w-3 h-3 rounded-full bg-amber-500 mt-0.5 flex-shrink-0"
                ></span>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Erzeugungsspitzen</strong
                  >
                  Dunkelgrüne, hohe Balken (Erzeugt): Die typische
                  Solar-Glockenkurve. Die Produktion startet gegen 05:00 Uhr
                  morgens, erreicht ihr Maximum zwischen 12:00 und 13:00 Uhr
                  (mit fast 1.500 kWh Gesamtertrag in dieser Stunde über den
                  Monat) und endet gegen 18:00 Uhr.
                </div>
              </div>
              <div
                class="flex items-start space-x-3 bg-white p-3 rounded-lg border border-slate-100 shadow-xs"
              >
                <span
                  class="w-3 h-3 rounded-full bg-blue-600 mt-0.5 flex-shrink-0"
                ></span>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Abdeckung durch lokalen PV-Strom</strong
                  >
                  Hellgrüner Balkenabschnitte (EEG): Das ist der direkt genutzte
                  Sonnenstrom. Sobald die Sonne scheint (ab 06:00 Uhr), wird der
                  Verbrauch fast vollständig durch die eigene Erzeugung gedeckt.
                </div>
                <div>
                  <strong class="text-slate-900 block mb-0.5"
                    >Netzbezug</strong
                  >
                  Pinke Balkenabschnitte (EVU): Das ist der "teure" Netzbezug. Er
                  dominiert logischerweise nachts und in den späten Abendstunden
                  (18:00 bis 05:00 Uhr), wenn die PV-Anlage keinen Strom
                  liefert.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leistungsübersicht (Auswahl-Box) -->
        <div
          class="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm space-y-4"
        >
          <h4
            class="text-sm font-bold text-slate-800 uppercase tracking-wider mb-2"
          >
            Leistungsübersicht
          </h4>

          <!-- Eintrag 1 -->
          <div
            @click="activeView = 'lastverlauf'"
            class="flex items-start space-x-4 group cursor-pointer p-2 rounded-lg hover:bg-white transition-colors"
          >
            <img
              :src="lastverlauf"
              class="w-24 h-16 object-cover rounded-lg border border-slate-300 group-hover:border-eeg-green transition-all flex-shrink-0"
              alt="Lastverlauf"
            />
            <div>
              <p
                class="text-sm font-semibold text-slate-900 group-hover:text-eeg-green transition-colors"
              >
                Lastverlauf (Mitglied)
              </p>
              <p class="text-xs text-slate-600">
                1/4 stündliche Leistungsmessung
                <span class="text-eeg-green font-medium"
                  >(Diagramm anzeigen)</span
                >
              </p>
            </div>
          </div>

          <hr class="border-slate-200" />

          <!-- Eintrag 2 -->
          <div
            @click="activeView = 'tagesverlauf'"
            class="flex items-start space-x-4 group cursor-pointer p-2 rounded-lg hover:bg-white transition-colors"
          >
            <img
              :src="tagesverlauf"
              class="w-24 h-16 object-cover rounded-lg border border-slate-300 group-hover:border-eeg-green transition-all flex-shrink-0"
              alt="Tagesverlauf"
            />
            <div>
              <p
                class="text-sm font-semibold text-slate-900 group-hover:text-eeg-green transition-colors"
              >
                Tagesverlauf Erzeugung
              </p>
              <p class="text-xs text-slate-600">
                Analyse der Dynamik der einzelnen Tage
                <span class="text-eeg-green font-medium"
                  >(Diagramm anzeigen)</span
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import ImageCard from './ImageCard.vue';
import sonneEnergie from '../assets/firstPage/sonnenEnergie.png';
import sonneTankstelle from '../assets/firstPage/sonneTankstelle.png';
import trafo from '../assets/trafo5002.png';
import trafoIcon from '../assets/firstPage/trafoIcon.png';
import lastverlauf from '../assets/firstPage/lastverlaufJuli2026.png';
import tagesverlauf from '../assets/firstPage/tagesverlaufJuli2026.png';

const activeView = ref('tra');
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped>
.enBild {
  width: 5%;
  height: auto;
}

h3 {
  margin: 40px 0 0;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
  margin: 0 10px;
}

a {
  color: #42b983;
}

div::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

div::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

div::-webkit-scrollbar-thumb {
  background: #10b981;
  /* Dein eeg-green */
  border-radius: 10px;
  border: 2px solid #f1f1f1;
}

div::-webkit-scrollbar-thumb:hover {
  background: #059669;
}
</style>
